import { makeAutoObservable, runInAction } from "mobx";
import { IAccountModel, IAccountSetting } from "../../../models/Account";
import AccountService from "../../../services/api/account-service";
import { rootContainer, TYPE } from "../../../services/container/root";
import { uuid } from "../../../services/helpers";
import { ISaveable } from "../../../stores/types";
import roleStore from "./role-store";
import { IIframeService } from "../../../services/iframe-service/iframe-service";
import AccountRolesEditor from "./account-roles-editor";


interface IAccountEditorModelChanges extends Partial<IAccountModel> {
    _client_id?: string; /** used when creating new account */
}

class AccountEditorStore implements ISaveable {
    private _model: IAccountModel = {} as IAccountModel;
    private _password: string = '';
    private _password_confirm: string = '';
    private _changes: IAccountEditorModelChanges = {};
    private _isSaving: boolean = false;

    private _accountRolesEditor: AccountRolesEditor;

    constructor(model: IAccountModel|undefined, private _isMyAccount:boolean) {
        if (model) {
            this._model = model;
        }
        else { /** when we create a new account - assign temporary client_id */
            this._changes._client_id = uuid();
        }

        this._accountRolesEditor = new AccountRolesEditor(this, roleStore, model?.acls);
        
        makeAutoObservable(this, {}, { autoBind: true });
    }

    public get accountRolesEditor(): AccountRolesEditor { return this._accountRolesEditor; }

    public get id(): number | string | undefined { return this._model?.id ?? this._changes._client_id; }
    public get store_user_id(): number | undefined { return this._model?.user_shops_settings?.[0]?.store_user_id; }
    public get email(): string { return this._changes?.email ?? this._model?.email; }
    public get displayEmail(): string { return this._model.display_email; }
    public get first_name(): string { return this._changes?.first_name ?? this._model?.first_name; }
    public get name(): string { return this._model?.name ?? ''; }
    public get surname(): string { return this._changes?.surname ?? this._model?.surname; }
    public get password(): string | undefined { return this._password; }
    public get password_confirm(): string | undefined { return this._password_confirm; }
    public get avatar_url(): string | undefined { return this._changes?.avatar_url ?? this._model?.avatar_url; }
    public get is_enabled(): boolean { 
        return !!(this._changes?.user_shops_settings?.[0]?.is_enabled ?? this._model?.user_shops_settings?.[0]?.is_enabled); 
    }
    public get isMyAccount() { return this._isMyAccount; }

    public get dashboardLocale(): string {
        return this._changes.dashboard_locale ?? this._model.dashboard_locale;
    }

    public setDashboardLocale(v: string) {
        this._changes.dashboard_locale = v;
        this._clearUnneededChanges();
    }

    public isIdEquals(id?: string|number): boolean {
        return this._model?.id === Number(id) || this._changes._client_id === (id ?? '').toString();
    }

    public getRoleNames(): string {
        return this._model?.acls?.map(acl => acl.role.display_name).join(', ');
    }

    public getPermissionNames(): string {
        return this._model?.acls?.map(
            acl => acl.role.permissions?.map(p => roleStore.getPermissionName(p.permission_id)).join(', ')
        ).join(', ');
    }

    public hasChanges(): boolean {
        const changes = { ...this._changes };
        delete changes._client_id;
        return !!Object.keys(changes).length || !!this.accountRolesEditor.hasChanges();
    }

    public discardChanges(): void {
        this._changes = {};
        this.accountRolesEditor.discardChanges();
    }

    public getUpdatedModel(): IAccountModel {
        const model = { ...this._model, ...this._changes };
        model._roles = this.accountRolesEditor.getUpdatedModel();
        return model;
    }

    public isSaving(): boolean {
        return this._isSaving;
    }

    private get service(): AccountService {
        return rootContainer.get<AccountService>(TYPE.AccountService);
    }

    private get iframeService(): IIframeService {
        return rootContainer.get<IIframeService>(TYPE.IframService);
    }

    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof IAccountModel;
            if (this._changes[theKey] === this._model?.[theKey]) {
                delete this._changes[theKey];
            }
        });
    }

    public setFirstName(value: string) {
        this._changes.first_name = value;
        this._clearUnneededChanges();
    }

    public setEmail(value: string) {
        this._changes.email = value;
        this._clearUnneededChanges();
    }

    public setName(value: string) {
        this._changes.email = value;
        this._clearUnneededChanges();
    }

    public setSurname(value: string) {
        this._changes.surname = value;
        this._clearUnneededChanges();
    }

    public getInitials(): string | undefined {
        let initials = undefined;
        /** settings initials only when both first name and surname exist, otherwise undefined - avatar should show no_avatar image */
        if (!isEmptyString(this.first_name) && !isEmptyString(this.surname)) {
            initials = this.first_name[0] + this.surname[0];
        }
        return initials;
    }

    public setPassword(value: string): void {
        this._password = value;
    }

    public setPasswordConfirm(value: string): void {
        this._password_confirm = value;
    }

    public async save(): Promise<void> {
        this._isSaving = true;

        try {
            let account: IAccountModel;
            if (this.isNew) {
                account = await this.service.createAccount(this.getUpdatedModel());
            } else {
                account = await this.service.updateAccount(this.id as number, this.getUpdatedModel());
            }
            runInAction(() => {
                this._isSaving = false;
                this.discardChanges();
                this._model = account;
                this._accountRolesEditor = new AccountRolesEditor(this, roleStore, account.acls);
            });
        } catch (error) {
            this._isSaving = false;
        }
    }

    public async saveProfile(): Promise<void> {
        this._isSaving = true;
        const updatedModel = this.getUpdatedModel();
        const redirectAfterSave = updatedModel.dashboard_locale !== this._model.dashboard_locale;

        const profile = await this.service.updateProfile(updatedModel);
        runInAction(() => {
            this._isSaving = false;
            this.discardChanges();
            this._model = profile;
        });
        if (redirectAfterSave) {
            this.iframeService.refreshParentFrame();
        }
    }


    public async toggleIsEnabled(): Promise<void> {
        this._changes = {
            ...this._changes,
            user_shops_settings: [{
                ...this._changes?.user_shops_settings?.[0],
                is_enabled: !this.is_enabled, store_user_id: this.store_user_id
            }]
        };
        return await this.updateAccountSetting();
    }

    public get canUpdateAccountSettings() {
        /** prevent from toggling is_enabled for current logged in user */
        return !this.isMyAccount;
    }

    public get isParentOwnerUser() {
        /** this checks if current account is marked as owner from parent shop */
        return this._model.user_shops_settings.some((setting) => {
            return !!setting.parent_store_id;
        });
    }

    private async updateAccountSetting(): Promise<void> {
        this._isSaving = true;
        const setting = this.getUpdatedModel().user_shops_settings[0];
        let updatedSetting: IAccountSetting;

        try {
            updatedSetting = await this.service.updateAccountSetting(this._model?.id, setting);
        }
        catch { }

        runInAction(() => {
            this._isSaving = false;
            if (updatedSetting) {
                this._model.user_shops_settings[0] = updatedSetting;
            }
            /** discard changes only for account sttings */
            delete this._changes.user_shops_settings;
        });
    }

    public async changePassword(): Promise<void> {
        this._isSaving = true;
        try {
            if (this.isMyAccount) {
                await this.service.changeUserPassword(this._password, this._password_confirm);
            }
            else {
                await this.service.changePasswordByUserId(this._model.id, this._password_confirm, this._password);
            }
            runInAction(() => {
                this._isSaving = false;
                this._password = '';
                this._password_confirm = '';
            });
        } catch (error) {
            this._isSaving = false;
        }

    }

     /** true when we created new account and not yet saved in DB */
     public get isNew(): boolean {
        return !this._model?.id;
    }
}

export default AccountEditorStore;


function isEmptyString(s: string): boolean {
    return s === undefined || s === null || s.trim() === '';
}
